import { inject, Injectable } from '@angular/core';
import { EPermission, PrivilegeService } from '@insolution/auth-data-access';
import {
  CreateTenantGQL,
  CreateTenantInput,
  ListTenantParentsGQL,
  ListTenantsByWorkshopTenantIdGQL,
  ListTenantsByWorkshopTenantIdQuery,
  ListTenantsGQL,
  ListWorkshopTenantsGQL,
  ListWorkshopTenantsQuery,
  Tenant,
} from '@insolution/graphql-data-access';
import { TenantForCreating } from '@insolution/shared-data-access';
import { firstValueFrom } from 'rxjs';
import { ITenantTreeTenant } from '../interfaces/tenant-tree-tenant.interface';

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private createTenantGQL = inject(CreateTenantGQL);
  private listTenantsGQL = inject(ListTenantsGQL);
  private listWorkshopTenantsGQL = inject(ListWorkshopTenantsGQL);
  private listTenantParentsGQL = inject(ListTenantParentsGQL);
  private listTenantsByWorkshopTenantIdGQL = inject(ListTenantsByWorkshopTenantIdGQL);
  private privilegeService = inject(PrivilegeService);

  public getTenantsByWorkshopTenantId(
    workshop_tenant_id: string
  ): Promise<ListTenantsByWorkshopTenantIdQuery['listTenantsByWorkshopTenantId']> {
    return firstValueFrom(this.listTenantsByWorkshopTenantIdGQL.fetch({ workshop_tenant_id })).then(
      (result) => result.data.listTenantsByWorkshopTenantId
    );
  }

  public findTenantByWorkshopTenantId(
    workshop_tenant_id: string,
    tenant_id: string
  ): Promise<ListTenantsByWorkshopTenantIdQuery['listTenantsByWorkshopTenantId'][0] | undefined> {
    // used for workshop only, because workshop can only fetch tenant list by workshop tenant id
    return this.getTenantsByWorkshopTenantId(workshop_tenant_id).then((tenants) =>
      tenants.find((tenant) => tenant.id === tenant_id)
    );
  }

  public async getTenantTree(): Promise<ITenantTreeTenant | null> {
    const canManageUsers = await this.privilegeService.hasPermission(EPermission.ManageUsers);
    const tenants = await this.getTenants();
    const tenantParents = (await this.getTenantParents()).map((parent) => ({
      ...parent,
      isParent: true,
    }));

    const rootTenant = tenantParents.find((tenant) => !tenant.parent_id) || tenants.find((tenant) => !tenant.parent_id);
    if (!rootTenant) {
      return null;
    }

    const childrenTree = this.buildTenantTree(
      rootTenant.id,
      [...tenantParents.map(this.convertTenantToTreeTenant), ...tenants.map(this.convertTenantToTreeTenant)],
      canManageUsers
    );

    return {
      ...rootTenant,
      disabled: !canManageUsers && childrenTree.length > 0,
      children: childrenTree,
    };
  }

  public async createTenant(tenantInput: TenantForCreating, currentTenant: Tenant): Promise<Tenant> {
    const input: CreateTenantInput = {
      ...tenantInput,
      parent_id: currentTenant.id,
      tenant_ref: `${currentTenant.tenant_ref}${tenantInput.id}.`,
      root_tenant_id: currentTenant.tenant_ref.split('.')[0],
    };
    return firstValueFrom(
      this.createTenantGQL.mutate(
        {
          input,
        },
        {
          update: (cache) => {
            cache.modify({
              fields: {
                listTenants(existing, { DELETE }) {
                  return DELETE;
                },
                listTenantsByWorkshopTenantId(existing, { DELETE }) {
                  return DELETE;
                },
                listTenantParents(existing, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        }
      )
    ).then((result) => {
      if (result.errors) {
        throw new Error(result.errors.map((error) => error.message).join(', '));
      }
      return result.data?.createTenant as Tenant;
    });
  }

  public getTenants(): Promise<Tenant[]> {
    return firstValueFrom(this.listTenantsGQL.fetch()).then((result) => result.data.listTenants);
  }

  public getTenantParents(): Promise<Tenant[]> {
    return firstValueFrom(this.listTenantParentsGQL.fetch()).then((result) => result.data.listTenantParents);
  }

  public getWorkshopTenants(): Promise<ListWorkshopTenantsQuery['listWorkshopTenants']> {
    return firstValueFrom(this.listWorkshopTenantsGQL.fetch()).then((result) => result.data.listWorkshopTenants);
  }

  private buildTenantTree(
    parentId: string,
    tenants: ITenantTreeTenant[],
    canManageUsers: boolean
  ): ITenantTreeTenant[] {
    const childTenants = tenants.filter((tenant) => tenant.parent_id === parentId);

    return childTenants.map((tenant) => {
      const childrenTree = this.buildTenantTree(tenant.id, tenants, canManageUsers);

      return {
        ...tenant,
        disabled: !canManageUsers && childrenTree.length > 0,
        children: childrenTree,
      };
    });
  }

  private convertTenantToTreeTenant(tenant: Tenant): ITenantTreeTenant {
    return {
      ...tenant,
      children: [],
    };
  }
}
